// src/services/guideCacheService.js
/**
 * Service for caching and retrieving guide content using IndexedDB
 * Enables offline access to previously viewed guides
 */

const DB_NAME = 'worldexplore_guides_db';
const DB_VERSION = 1;
const STORE_NAME = 'guides';

/**
 * Initialize the IndexedDB database
 * @returns {Promise<IDBDatabase>} The database instance
 */
const initializeDB = () => {
  return new Promise((resolve, reject) => {
    // Check if IndexedDB is supported and if we're in a browser environment
    if (typeof window === 'undefined' || !window.indexedDB) {
      reject(new Error('IndexedDB not supported'));
      return;
    }

    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    
    // Handle database upgrade (first creation or version change)
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'codeNumber' });
        
        // Create indexes for faster queries
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('type', 'guideType', { unique: false });
      }
    };
    
    request.onerror = (event) => {
      reject(event.target.error);
    };
    
    request.onblocked = (event) => {
      reject(new Error('IndexedDB access blocked'));
    };
    
    request.onsuccess = (event) => {
      const db = event.target.result;
      
      // Add error handler to connection
      db.onerror = (event) => {
        // Silent error handling
      };
      
      resolve(db);
    };
  });
};

/**
 * Store guide content in cache
 * @param {string} codeNumber - The code used to access the guide
 * @param {Object} guideData - The guide content to store
 * @param {string} guideType - The type of guide (tourist, accommodation)
 * @param {Object} partnerData - Associated partner data
 * @returns {Promise<boolean>} Success status
 */
export const cacheGuide = async (codeNumber, guideData, guideType, partnerData) => {
  try {
    const db = await initializeDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      // Prepare data for storage
      const entry = {
        codeNumber,
        guideData,
        guideType,
        partnerData,
        timestamp: Date.now(),
      };
      
      // Store the guide
      const request = store.put(entry);
      
      request.onerror = (event) => {
        reject(event.target.error);
      };
      
      request.onsuccess = () => {
        resolve(true);
      };
    });
  } catch (error) {
    return false;
  }
};

/**
 * Retrieve cached guide content by code number
 * @param {string} codeNumber - The code used to access the guide
 * @returns {Promise<Object|null>} The cached guide data or null if not found
 */
export const getCachedGuide = async (codeNumber) => {
  try {
    if (!codeNumber) {
      return null;
    }
    
    const db = await initializeDB();
    if (!db) {
      return null;
    }
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      
      // Get the guide by code number
      const request = store.get(codeNumber);
      
      request.onerror = (event) => {
        reject(event.target.error);
      };
      
      request.onsuccess = (event) => {
        const result = event.target.result;
        if (result) {
          // Update access timestamp (in a separate transaction)
          updateAccessTimestamp(codeNumber).catch(err => {
            // Silent error handling
          });
        }
        resolve(result || null);
      };
    });
  } catch (error) {
    return null;
  }
};

/**
 * Update the access timestamp for a guide
 * @param {string} codeNumber - The code number of the guide
 * @returns {Promise<boolean>} Success status
 */
const updateAccessTimestamp = async (codeNumber) => {
  const db = await initializeDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    // First get the current data
    const getRequest = store.get(codeNumber);
    
    getRequest.onsuccess = (event) => {
      const data = event.target.result;
      if (data) {
        // Update the timestamp
        data.timestamp = Date.now();
        
        // Put it back
        const updateRequest = store.put(data);
        
        updateRequest.onsuccess = () => {
          resolve(true);
        };
        
        updateRequest.onerror = (event) => {
          reject(event.target.error);
        };
      } else {
        resolve(false);
      }
    };
    
    getRequest.onerror = (event) => {
      reject(event.target.error);
    };
  });
};

/**
 * Get all cached guides
 * @returns {Promise<Array>} Array of cached guides
 */
export const getAllCachedGuides = async () => {
  try {
    const db = await initializeDB();
    if (!db) {
      return [];
    }
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      
      // Get all guides
      const request = store.getAll();
      
      request.onerror = (event) => {
        reject(event.target.error);
      };
      
      request.onsuccess = (event) => {
        const guides = event.target.result || [];
        resolve(guides);
      };
    });
  } catch (error) {
    return [];
  }
};

/**
 * Delete a cached guide
 * @param {string} codeNumber - The code number of the guide to delete
 * @returns {Promise<boolean>} Success status
 */
export const deleteCachedGuide = async (codeNumber) => {
  try {
    const db = await initializeDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      // Delete the guide
      const request = store.delete(codeNumber);
      
      request.onerror = (event) => {
        reject(event.target.error);
      };
      
      request.onsuccess = () => {
        resolve(true);
      };
    });
  } catch (error) {
    return false;
  }
};

/**
 * Clear all cached guides
 * @returns {Promise<boolean>} Success status
 */
export const clearCachedGuides = async () => {
  try {
    const db = await initializeDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      // Clear all guides
      const request = store.clear();
      
      request.onerror = (event) => {
        reject(event.target.error);
      };
      
      request.onsuccess = () => {
        resolve(true);
      };
    });
  } catch (error) {
    return false;
  }
};

/**
 * Check if database is available (for offline detection)
 * @returns {Promise<boolean>} Whether the database is available
 */
export const isDatabaseAvailable = async () => {
  try {
    await initializeDB();
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get the estimated size of cached guides
 * @returns {Promise<number>} Size in bytes
 */
export const getCachedGuidesSize = async () => {
  try {
    const guides = await getAllCachedGuides();
    // Approximate size by stringifying
    const jsonString = JSON.stringify(guides);
    return jsonString.length * 2; // Unicode characters can be 2 bytes
  } catch (error) {
    return 0;
  }
};

/**
 * Get information about guide caching
 * @returns {Promise<Object>} Cache statistics
 */
export const getCacheStats = async () => {
  try {
    const guides = await getAllCachedGuides();
    return {
      count: guides.length,
      sizeBytes: await getCachedGuidesSize(),
      oldestGuide: guides.length > 0 ? 
        guides.reduce((oldest, guide) => 
          guide.timestamp < oldest.timestamp ? guide : oldest
        , guides[0]) : null,
      newestGuide: guides.length > 0 ?
        guides.reduce((newest, guide) => 
          guide.timestamp > newest.timestamp ? guide : newest
        , guides[0]) : null
    };
  } catch (error) {
    return {
      count: 0,
      sizeBytes: 0,
      oldestGuide: null,
      newestGuide: null
    };
  }
};
